import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSClosures: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Closures</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    A function that remembers the variables from where it was defined — even after that outer function has returned. Closures power private state, factories, modules, hooks, and more.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="17 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine a banking app with a function that creates an account:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createAccount() {\n  let balance = 0;\n  return {\n    deposit(amount) { balance += amount; },\n    getBalance() { return balance; },\n  };\n}\n\nconst account = createAccount();\naccount.deposit(100);\nconsole.log(account.getBalance());` }]} defaultLanguage="javascript" id="cl-bank" />
   <Output>{`100`}</Output>
   <Callout tone="info" title="The puzzle">
    <Code>createAccount()</Code> already finished. Local variables should disappear when a function ends — so why does <Code>balance</Code> still exist? The answer is <Mark>closures</Mark>.
   </Callout>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why This Happens</SectionHeading>
   <Para>Normally a function&apos;s locals die when it ends, so memory can be reclaimed:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Function starts → count created → count used → function ends → count removed` }]} defaultLanguage="text" id="cl-normal" />
   <Para>But when an inner function is returned and still needs an outer variable, JavaScript must decide: should the variable disappear, or survive?</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function outer() {\n  let count = 10;\n  function inner() {\n    console.log(count); // still needs count\n  }\n  return inner;\n}` }]} defaultLanguage="javascript" id="cl-survive" />
   <Para>JavaScript chooses <Mark>survive</Mark>, because the returned function still references it. That survival is a closure.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>closure</Mark> is created when a function remembers and keeps accessing variables from its lexical scope even after the outer function has finished executing.</p>
   </div>
   <Para>In short: <Mark>function + remembered variables = closure.</Mark></Para>
  </section>

  {/* 4. Core Idea: lexical scope */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea: Lexical Scope</SectionHeading>
   <Para>Closures are built on lexical scope — a function can access variables from its own scope, the scope it was defined in, and outward to the global scope:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const globalVar = "Global";\nfunction outer() {\n  const outerVar = "Outer";\n  function inner() {\n    const innerVar = "Inner";\n    console.log(globalVar); // ✓ global\n    console.log(outerVar);  // ✓ outer\n    console.log(innerVar);  // ✓ inner\n  }\n  inner();\n}` }]} defaultLanguage="javascript" id="cl-lexical" />
   <Para>That outward lookup chain is <Mark>lexical scope</Mark> — and closures are what keep it alive after the outer function returns.</Para>
  </section>

  {/* 5. Internal Working */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function outer() {\n  let count = 0;\n  function inner() {\n    count++;\n    console.log(count);\n  }\n  return inner;\n}\n\nconst counter = outer();\ncounter();\ncounter();\ncounter();` }]} defaultLanguage="javascript" id="cl-counter" />
   <Output>{`1\n2\n3`}</Output>
   <ol className="my-4 space-y-1.5 text-[14px] text-gray-700 dark:text-gray-300 list-decimal list-inside">
    <li><Code>outer()</Code> runs, creating <Code>count = 0</Code>.</li>
    <li><Code>inner()</Code> is created and remembers its lexical scope.</li>
    <li><Code>outer()</Code> returns — normally <Code>count</Code> would be freed.</li>
    <li>But <Code>inner</Code> still references <Code>count</Code>, so JavaScript keeps it alive.</li>
    <li>Every <Code>counter()</Code> call uses the <Mark>same</Mark> <Code>count</Code> — not a fresh one.</li>
   </ol>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `outer()\n  ↓\ncount = 0\n  ↓\nreturn inner\n  ↓\nouter ends  ──  BUT count survives (inner still references it)\n  ↓\nclosure created` }]} defaultLanguage="text" id="cl-visual" />
  </section>

  {/* 7. Simple Example: reference not value */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example: Reference, Not a Copy</SectionHeading>
   <Para>A common misconception is that a closure stores a <em>copy</em> of the value. It stores a <Mark>reference to the variable</Mark> — so multiple functions share the same one:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function outer() {\n  let count = 0;\n  return {\n    increment() { count++; },\n    print() { console.log(count); },\n  };\n}\n\nconst counter = outer();\ncounter.increment();\ncounter.increment();\ncounter.print();` }]} defaultLanguage="javascript" id="cl-ref" />
   <Output>{`2`}</Output>
   <Para>Both methods close over the same <Code>count</Code>, so changes from one are visible to the other.</Para>
  </section>

  {/* 8. Deep Example: private state */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Private State</SectionHeading>
   <Para>One of the most practical uses — data that can only be touched through the methods you expose:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createBankAccount() {\n  let balance = 0; // private\n  return {\n    deposit(amount) { balance += amount; },\n    withdraw(amount) { balance -= amount; },\n    getBalance() { return balance; },\n  };\n}\n\nconst account = createBankAccount();\naccount.deposit(500);\nconsole.log(account.getBalance()); // 500\nconsole.log(account.balance);      // undefined — hidden!` }]} defaultLanguage="javascript" id="cl-private" />
   <Para>There&apos;s no way to read or corrupt <Code>balance</Code> from outside — only the closure can reach it. This is how closures create true private state.</Para>
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Examples</SectionHeading>
   <SubHeading>Rate limiter (state across calls)</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createLimiter(limit) {\n  let count = 0;\n  return function () {\n    if (count >= limit) return console.log("Limit Reached");\n    count++;\n    console.log(\`Request \${count}\`);\n  };\n}\n\nconst limiter = createLimiter(3);\nlimiter(); limiter(); limiter(); limiter();` }]} defaultLanguage="javascript" id="cl-limiter" />
   <Output>{`Request 1\nRequest 2\nRequest 3\nLimit Reached`}</Output>
   <SubHeading>Module pattern &amp; factory functions</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Module pattern (private data before ES modules)\nconst UserModule = (function () {\n  let users = [];\n  return {\n    addUser(user) { users.push(user); },\n    getUsers() { return users; },\n  };\n})();\n\n// Factory function — the object remembers 'name'\nfunction createUser(name) {\n  return { greet() { console.log(\`Hello \${name}\`); } };\n}\ncreateUser("Sachin").greet(); // Hello Sachin` }]} defaultLanguage="javascript" id="cl-module" />
   <div className="flex flex-wrap gap-2 mt-3">{['Private variables', 'Module pattern', 'Factory functions', 'Event handlers', 'Memoization / caching', 'React hooks', 'Rate limiting'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 10. Engineering Tradeoffs: the loop bug */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs: The Famous Loop Bug</SectionHeading>
   <Para>The single most famous closure interview question:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (var i = 1; i <= 3; i++) {\n  setTimeout(() => console.log(i), 1000);\n}` }]} defaultLanguage="javascript" id="cl-bug" />
   <Output>{`4\n4\n4`}</Output>
   <Para><Code>var</Code> is function-scoped, so there is <Mark>one shared</Mark> <Code>i</Code>. All three callbacks close over the same variable, and by the time they run the loop has finished with <Code>i = 4</Code>. Using <Code>let</Code> creates a fresh binding per iteration:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 1; i <= 3; i++) {\n  setTimeout(() => console.log(i), 1000);\n}\n// 1  2  3` }]} defaultLanguage="javascript" id="cl-fix" />
  </section>

  {/* 11. Performance Considerations: memory leaks */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations: Memory Leaks</SectionHeading>
   <Para>Closures keep variables alive — which can be a liability. A closure over a large object prevents it from being garbage collected for as long as the closure lives:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createLargeData() {\n  const hugeArray = new Array(1_000_000);\n  return function () {\n    console.log(hugeArray.length); // keeps hugeArray in memory\n  };\n}` }]} defaultLanguage="javascript" id="cl-leak" />
   <Callout tone="warning" title="Leak risk">
    Even after <Code>createLargeData()</Code> returns, <Code>hugeArray</Code> stays in memory because the closure references it. Release closures (and detach event handlers) you no longer need so their captured data can be collected.
   </Callout>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="var in loops">Sharing one variable across iterations — use <Code>let</Code> or an IIFE.</Callout>
   <Callout tone="warning" title="Assuming a value snapshot">Closures capture the variable, not its value at creation time.</Callout>
   <Callout tone="warning" title="Unintended memory retention">Holding large data alive through long-lived closures or handlers.</Callout>
   <Callout tone="warning" title="Stale closures in React">A callback capturing an old state/prop value because of a missing dependency.</Callout>
  </section>

  {/* 13. Debugging Stories: React + events */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The stale React state">
    A <Code>useEffect</Code> with an empty dependency array logged the <em>initial</em> count forever:
   </Callout>
   <CodeBlock snippets={[{ language: 'javascript', code: `useEffect(() => {\n  const id = setInterval(() => console.log(count), 1000);\n  return () => clearInterval(id);\n}, []); // [] → closure captured the first 'count'` }]} defaultLanguage="javascript" id="cl-react" />
   <Para>The interval&apos;s callback closed over <Code>count</Code> from the first render. Adding <Code>count</Code> to the dependency array (or using a functional update) gave it the fresh value. Many &quot;React bugs&quot; are really closure bugs.</Para>
   <Callout tone="info" title="The handler that remembered">
    An event handler kept logging an old <Code>id</Code> because it closed over the variable from when it was attached — exactly the behaviour that makes closures useful, but surprising if unexpected.
   </Callout>
  </section>

  {/* 14. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is a closure?', 'Why do closures exist?', 'What is lexical scope?', 'Do closures store values or variables?', 'Why does the loop print 4 4 4?', 'How can closures cause leaks?', 'How are closures used in React?', 'Closure vs scope?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Do closures store the value or the variable?">
    The variable (a live reference) — not a snapshot of the value. Multiple closures over the same variable see each other&apos;s changes, which is why the shared-counter example prints the latest value.
   </QA>
   <QA q="Why does the var loop print 4 4 4?">
    <Code>var</Code> is function-scoped, so all iterations share one <Code>i</Code>. The async callbacks run after the loop ends, when <Code>i</Code> is 4. <Code>let</Code> creates a new binding each iteration, giving 1 2 3.
   </QA>
   <QA q="How can closures cause memory leaks?">
    A closure keeps its captured variables alive as long as the closure is reachable. Closing over large objects, or never detaching event handlers/timers, can keep that memory from being garbage collected.
   </QA>
   <QA q="What's the difference between a closure and scope?">
    Scope is the set of variables accessible at a point in code. A closure is the runtime pairing of a function with the scope it was defined in, preserved so the function can use those variables even after the outer function returns.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>A function carries a <Mark>backpack</Mark> of the variables from where it was created. Even after it leaves home (the outer function returns), it still carries that backpack everywhere it goes. That backpack is the closure.</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A closure is created when a function remembers variables from its lexical scope even after the outer function has finished executing.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Closures aren&apos;t a special feature — they&apos;re a natural consequence of lexical scope. Whenever a function outlives its original scope and still accesses outer variables, a closure forms. They enable private state, factory functions, modules, event handlers, React hooks, caching, and many advanced patterns.
   </Callout>
  </section>
 </div>
);

export default JSClosures;
